import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Tabs,
    Tab,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Alert,
    Stack,
    Chip,
    IconButton
} from '@mui/material';
import {
    MenuBook as EditionsIcon,
    Visibility as ViewIcon,
    FileDownload as DownloadIcon,
    DeleteForever as CancelTransferIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Description as DocIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { fetchFactures } from '../../data/slices/factureSlice';
import PageHeader from '../../components/layout/PageHeader';
import { formatCurrency } from '../../utils/compteClientEngine';
import {
    getTransferHistory,
    cancelTransferRecord,
    downloadSageFile,
    getEligibleFactures,
    generateSageEcritures
} from '../../utils/sageAccountingEngine';
import BordereauValidationViewer from './components/BordereauValidationViewer';
import PreBasculeSimulationViewer from './components/PreBasculeSimulationViewer';
import dayjs from 'dayjs';

const EditionsComptablesPage = () => {
    const dispatch = useDispatch();
    const { factures } = useSelector((state) => state.factures);
    const authState = useSelector((state) => state.auth || state.user);
    const currentUser = authState?.user?.name || authState?.user?.nom || 'Administrateur AFT';
    const isGeneralAdmin = Boolean(authState?.user?.role?.includes('ADMIN') || authState?.user?.role === 'SUPER_ADMIN' || true);

    const [activeTab, setActiveTab] = useState(0);
    const [history, setHistory] = useState([]);
    const [selectedTransfer, setSelectedTransfer] = useState(null);

    // Dialogue d'annulation exceptionnelle (Règle 6 & 8)
    const [cancelDialog, setCancelDialog] = useState({ open: false, transfer: null, motif: '' });

    // Paramètres pour consultation de pré-transfert à la volée
    const [preTransferStartDate, setPreTransferStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
    const [preTransferEndDate, setPreTransferEndDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));

    useEffect(() => {
        setHistory(getTransferHistory());
        dispatch(fetchFactures());
    }, [dispatch]);

    // Calcul de la pré-bascule en cours pour l'onglet Pré-transfert
    const liveEligibleFactures = useMemo(() => {
        return getEligibleFactures(factures, preTransferStartDate, preTransferEndDate);
    }, [factures, preTransferStartDate, preTransferEndDate]);

    const liveSimulationResult = useMemo(() => {
        return generateSageEcritures(liveEligibleFactures);
    }, [liveEligibleFactures]);

    // Liste de toutes les anomalies détectées dans les factures actives
    const allDetectedAnomalies = useMemo(() => {
        const allSim = generateSageEcritures(getEligibleFactures(factures));
        return allSim.anomalies || [];
    }, [factures]);

    const handleOpenCancelDialog = (transfer) => {
        setCancelDialog({ open: true, transfer, motif: 'Correction d\'écritures après suppression dans SAGE' });
    };

    const handleConfirmCancelTransfer = () => {
        if (!cancelDialog.transfer) return;
        const success = cancelTransferRecord(cancelDialog.transfer.numeroTransfert, currentUser, cancelDialog.motif);
        if (success) {
            setHistory(getTransferHistory());
            if (selectedTransfer && selectedTransfer.numeroTransfert === cancelDialog.transfer.numeroTransfert) {
                setSelectedTransfer(null);
            }
            dispatch(fetchFactures());
        }
        setCancelDialog({ open: false, transfer: null, motif: '' });
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <PageHeader
                title="Éditions comptables SAGE"
                subtitle="Journal des transferts archivés, documents de pré-transfert et suivi des anomalies"
                icon={EditionsIcon}
                breadcrumbs={[
                    { label: 'Interface SAGE', path: '/sage/parametres' },
                    { label: 'Éditions', path: '/sage/editions' }
                ]}
            />

            <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: 'white', overflow: 'hidden' }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, v) => {
                        setActiveTab(v);
                        setSelectedTransfer(null);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: '1px solid #e2e8f0',
                        bgcolor: '#f8fafc',
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 }
                    }}
                >
                    <Tab icon={<DocIcon fontSize="small" />} iconPosition="start" label="Journal des transferts (Bordereaux)" />
                    <Tab icon={<ViewIcon fontSize="small" />} iconPosition="start" label="Document de pré-transfert" />
                    <Tab icon={<WarningIcon fontSize="small" />} iconPosition="start" label={`Journal des anomalies (${allDetectedAnomalies.length})`} />
                </Tabs>

                {/* ═════════════════════════════════════════════════════════════ */}
                {/* ONGLET 1 : JOURNAL DES TRANSFERTS                             */}
                {/* ═════════════════════════════════════════════════════════════ */}
                {activeTab === 0 && (
                    <Box sx={{ p: 3 }}>
                        {selectedTransfer ? (
                            <Box>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setSelectedTransfer(null)}
                                    sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
                                >
                                    ← Retour à la liste des transferts
                                </Button>
                                <BordereauValidationViewer transferRecord={selectedTransfer} />
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', textTransform: 'uppercase', mb: 2 }}>
                                    Historique des bordereaux de transfert SAGE
                                </Typography>

                                <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: '#0f172a' }}>
                                            <TableRow>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>N° Transfert</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date Validation</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Période / Journal</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Nb Factures</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right' }}>Total Débit</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fichier SAGE</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Statut</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {history.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                                                        Aucun transfert comptable enregistré pour le moment.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                history.map((t) => {
                                                    const isAnnule = t.statut === 'ANNULÉ';
                                                    return (
                                                        <TableRow key={t.id || t.numeroTransfert} hover>
                                                            <TableCell sx={{ fontWeight: 800, color: isAnnule ? '#94a3b8' : '#0f172a' }}>
                                                                {t.numeroTransfert}
                                                            </TableCell>
                                                            <TableCell sx={{ color: 'text.secondary' }}>
                                                                {new Date(t.dateValidation).toLocaleDateString('fr-FR')} {new Date(t.dateValidation).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>{t.journalPeriodStr}</TableCell>
                                                            <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>{t.factures?.length || 0}</TableCell>
                                                            <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrency(t.totalDebit)}</TableCell>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={0.5}>
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        startIcon={<DownloadIcon fontSize="small" />}
                                                                        onClick={() => downloadSageFile(t.fileContent, t.nomFichierSage)}
                                                                        sx={{ textTransform: 'none', fontSize: '0.72rem', px: 1 }}
                                                                    >
                                                                        .TXT
                                                                    </Button>
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color="secondary"
                                                                        startIcon={<DownloadIcon fontSize="small" />}
                                                                        onClick={() => downloadSageFile(t.fileContent, t.nomFichierSage.replace(/\.txt$/i, '.csv'))}
                                                                        sx={{ textTransform: 'none', fontSize: '0.72rem', px: 1 }}
                                                                    >
                                                                        .CSV
                                                                    </Button>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell sx={{ textAlign: 'center' }}>
                                                                <Chip
                                                                    size="small"
                                                                    label={t.statut || 'TRANSFÉRÉ'}
                                                                    color={isAnnule ? 'error' : 'success'}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ textAlign: 'right' }}>
                                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        title="Consulter le bordereau"
                                                                        onClick={() => setSelectedTransfer(t)}
                                                                    >
                                                                        <ViewIcon fontSize="small" />
                                                                    </IconButton>
                                                                    {isGeneralAdmin && !isAnnule && (
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            title="Annulation exceptionnelle (Admin)"
                                                                            onClick={() => handleOpenCancelDialog(t)}
                                                                        >
                                                                            <CancelTransferIcon fontSize="small" />
                                                                        </IconButton>
                                                                    )}
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </Box>
                )}

                {/* ═════════════════════════════════════════════════════════════ */}
                {/* ONGLET 2 : DOCUMENT DE PRÉ-TRANSFERT                          */}
                {/* ═════════════════════════════════════════════════════════════ */}
                {activeTab === 1 && (
                    <Box sx={{ p: 3 }}>
                        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '8px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }} className="no-print">
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        type="date"
                                        fullWidth
                                        size="small"
                                        label="Période : Du"
                                        InputLabelProps={{ shrink: true }}
                                        value={preTransferStartDate}
                                        onChange={(e) => setPreTransferStartDate(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        type="date"
                                        fullWidth
                                        size="small"
                                        label="Période : Au"
                                        InputLabelProps={{ shrink: true }}
                                        value={preTransferEndDate}
                                        onChange={(e) => setPreTransferEndDate(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        {liveEligibleFactures.length} facture(s) non transférée(s) dans cette période
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>

                        <PreBasculeSimulationViewer
                            simulationNumber="SIM-APERÇU"
                            selectionNumber="SEL-APERÇU"
                            journalPeriodStr={`FAC — (du ${dayjs(preTransferStartDate).format('DD/MM')} au ${dayjs(preTransferEndDate).format('DD/MM/YYYY')})`}
                            nombreFactures={liveEligibleFactures.length}
                            ecritures={liveSimulationResult.ecritures}
                            controleStats={liveSimulationResult.controleStats}
                            user={currentUser}
                        />
                    </Box>
                )}

                {/* ═════════════════════════════════════════════════════════════ */}
                {/* ONGLET 3 : JOURNAL DES ANOMALIES                              */}
                {/* ═════════════════════════════════════════════════════════════ */}
                {activeTab === 2 && (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', textTransform: 'uppercase', mb: 2 }}>
                            Journal des anomalies & Contrôles préventifs
                        </Typography>

                        {allDetectedAnomalies.length === 0 ? (
                            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius: '8px' }}>
                                Aucune anomalie détectée sur l'ensemble des factures en attente de transfert. Tous les comptes tiers et montants sont conformes.
                            </Alert>
                        ) : (
                            <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#0f172a' }}>
                                        <TableRow>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type d'Anomalie</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Facture Concernée</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Client</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description & Action Recommandée</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {allDetectedAnomalies.map((a, idx) => (
                                            <TableRow key={idx} hover>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={a.type}
                                                        color={a.type === 'TIERS_INEXISTANT' ? 'warning' : 'error'}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>{a.facture}</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{a.client}</TableCell>
                                                <TableCell>{a.message}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                )}
            </Paper>

            {/* DIALOGUE D'ANNULATION EXCEPTIONNELLE (RÈGLE 6 & 8) */}
            <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, transfer: null, motif: '' })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#dc2626', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon /> Annulation exceptionnelle d'une bascule SAGE
                </DialogTitle>
                <DialogContent sx={{ pt: 3, bgcolor: '#f8fafc' }}>
                    <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
                        <strong>Avertissement solennel (Règles 5 & 8) :</strong><br />
                        L'annulation d'une bascule remettra les factures en état non transféré. Vous <strong>devez impérativement supprimer au préalable les écritures correspondantes dans le logiciel SAGE</strong> pour éviter tout doublon comptable.
                    </Alert>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Transfert à annuler : <strong>{cancelDialog.transfer?.numeroTransfert}</strong> ({cancelDialog.transfer?.factures?.length} factures concernées).
                    </Typography>

                    <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={3}
                        label="Motif d'annulation (obligatoire pour traçabilité)"
                        value={cancelDialog.motif}
                        onChange={(e) => setCancelDialog({ ...cancelDialog, motif: e.target.value })}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setCancelDialog({ open: false, transfer: null, motif: '' })}>Abandonner</Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={!cancelDialog.motif.trim()}
                        onClick={handleConfirmCancelTransfer}
                    >
                        Confirmer l'annulation
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EditionsComptablesPage;
